import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { currencyFormatter } from '../../utils/currencyFormatter';

const TabelaCruzamentoFluxoMensalPropTemporalEmMeses = ({
  linhasGastos = [],
  linhasIncome = [],
  valoresPorLinhaEColuna = {},
  quantidadeColunas = 0,
  valorTotalIncome = 0,
  percentualDespesasFiscais = 0,
  percentualComissao = 0,
  percentualCustoFinanceiro = 0,
  percentualTaxaAdministrativa = 0,
  percentualTaxaLucro = 0,
  percentualTaxaEncargos = 0,
  chaveValorGasto = 'propOrcItem_valor',
  chaveValorIncome = 'eventoPagamentoProp_vlr',
  chavePrazoIncome = 'eventoPagamentoProp_prazoPgto',
  chaveIdentificadorGasto,
  setPrecoFinalProposta,
  setResumoValoresProposta,
  visaoGeralValorTaxaEncargos,
  custosSemEncargos,
}) => {
  const [mostrarDetalhamentoTotalPeriodo, setMostrarDetalhamentoTotalPeriodo] = useState(false);

  const indicesColunas = useMemo(
    () => Array.from({ length: quantidadeColunas }, (_, index) => index + 1),
    [quantidadeColunas]
  );

  const toNumber = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.').trim();
      const parsed = Number(normalized);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const getLinhaGastoKey = (linha, indiceLinha) => linha?.id ?? linha?.[chaveIdentificadorGasto] ?? indiceLinha;

  const getIndiceColunaDoPrazo = (prazoRaw) => {
    const prazo = toNumber(prazoRaw);
    if (Number.isNaN(prazo) || prazo < 0) return null;
    if (prazo <= 30) return 1;
    return Math.ceil(prazo / 30);
  };

  const getTotalGastoPorMes = (indiceColuna) => {
    return linhasGastos.reduce((soma, linha, indiceLinha) => {
      const linhaKey = getLinhaGastoKey(linha, indiceLinha);
      const quantidade = toNumber(valoresPorLinhaEColuna?.[linhaKey]?.[indiceColuna] || 0);
      const valorUnitario = toNumber(linha?.[chaveValorGasto] || 0);
      return soma + (quantidade * valorUnitario);
    }, 0);
  };

  const getTotalIncomePorMesComBase = (indiceColuna, valorTotalBase) => {
    const totalBase = toNumber(valorTotalBase);
    return linhasIncome.reduce((soma, linha) => {
      const indiceColunaDoEvento = getIndiceColunaDoPrazo(linha?.[chavePrazoIncome]);
      if (indiceColunaDoEvento !== indiceColuna) return soma;

      const percentual = toNumber(linha?.[chaveValorIncome]);
      return soma + ((percentual / 100) * totalBase);
    }, 0);
  };

  const getTotalEncargosEquipeCltPorMes = (indiceColuna, taxaEncargos) => {
    return linhasGastos.reduce((soma, linha, indiceLinha) => {
      if (!Boolean(linha?.propOrcItem_equipeClt)) return soma;

      const linhaKey = getLinhaGastoKey(linha, indiceLinha);
      const quantidade = toNumber(valoresPorLinhaEColuna?.[linhaKey]?.[indiceColuna] || 0);
      if (quantidade <= 0) return soma;

      const valorUnitarioAtual = toNumber(linha?.[chaveValorGasto] || 0);
      const valorBase = (taxaEncargos > 0 ? (valorUnitarioAtual * (taxaEncargos / 100)) : valorUnitarioAtual);

      //const encargosUnitarios = valorBase * (taxaEncargos / 100);
      return soma + (valorBase * quantidade);
    }, 0);
  };

  const taxaDespesasFiscais = toNumber(percentualDespesasFiscais);
  const taxaComissao = toNumber(percentualComissao);
  const taxaCustoFinanceiro = toNumber(percentualCustoFinanceiro);
  const taxaAdministrativa = toNumber(percentualTaxaAdministrativa);
  const taxaLucro = toNumber(percentualTaxaLucro);
  const taxaEncargos = toNumber(percentualTaxaEncargos);
  const fatorReceita = 1 - (taxaComissao / 100) - (taxaDespesasFiscais / 100);

  const calcularFluxoMensal = (valorTotalBaseReceita) => {
    const resultado = [];
    let saldoPeriodoAnterior = 0;
    let saldoAcumuladoAnterior = 0;

    indicesColunas.forEach((indiceColuna) => {
      const gasto = getTotalGastoPorMes(indiceColuna);
      const income = getTotalIncomePorMesComBase(indiceColuna, valorTotalBaseReceita);
      const gastoEquipeCltEncargos = getTotalEncargosEquipeCltPorMes(indiceColuna, taxaEncargos);
      const custosDiretos = gasto;
      const custoAdministrativoMes = (gasto + gastoEquipeCltEncargos) * (taxaAdministrativa / 100);
      const custoOperacionalMes = (gasto + gastoEquipeCltEncargos) + custoAdministrativoMes;
      const lucroDesejadoMes = custoOperacionalMes * (taxaLucro / 100);
      const despesasFiscaisMes = income * (taxaDespesasFiscais / 100);
      const subtotalProjetoMes = custoOperacionalMes + lucroDesejadoMes;
      const comissaoMes = income * (taxaComissao / 100);
      const custoFinanceiroMes = saldoPeriodoAnterior < 0
        ? (saldoPeriodoAnterior * (taxaCustoFinanceiro / 100))*(-1)
        : 0;
      const saldoPeriodoMes = income - custoOperacionalMes - lucroDesejadoMes - custoFinanceiroMes - despesasFiscaisMes - comissaoMes;
      const saldoAcumuladoMes = saldoAcumuladoAnterior + saldoPeriodoMes;

      resultado.push({
        indiceColuna,
        gasto,
        income,
        gastoEquipeCltEncargos,
        custosDiretos,
        custoAdministrativo: custoAdministrativoMes,
        custoOperacional: custoOperacionalMes,
        lucroDesejado: lucroDesejadoMes,
        despesasFiscais: despesasFiscaisMes,
        comissao: comissaoMes,
        custoFinanceiro: custoFinanceiroMes,
        saldoPeriodo: saldoPeriodoMes,
        saldoAcumulado: saldoAcumuladoMes,
        subtotalProjeto: subtotalProjetoMes
      });

      saldoPeriodoAnterior = saldoPeriodoMes;
      saldoAcumuladoAnterior = saldoAcumuladoMes;
    });

    const totais = resultado.reduce((acc, mes) => ({
      gasto: acc.gasto + mes.gasto,
      gastoEquipeCltEncargos: acc.gastoEquipeCltEncargos + mes.gastoEquipeCltEncargos,
      custosDiretos: acc.custosDiretos + mes.custosDiretos,
      subtotalProjeto: acc.subtotalProjeto + mes.subtotalProjeto,
      income: acc.income + mes.income,
      custoAdministrativo: acc.custoAdministrativo + mes.custoAdministrativo,
      custoOperacional: acc.custoOperacional + mes.custoOperacional,
      lucroDesejado: acc.lucroDesejado + mes.lucroDesejado,
      despesasFiscais: acc.despesasFiscais + mes.despesasFiscais,
      comissao: acc.comissao + mes.comissao,
      custoFinanceiro: acc.custoFinanceiro + mes.custoFinanceiro,
      saldoPeriodo: acc.saldoPeriodo + mes.saldoPeriodo,
      saldoAcumulado: mes.saldoAcumulado,
    }), {
      gasto: 0,
      gastoEquipeCltEncargos: 0,
      custosDiretos: 0,
      subtotalProjeto: 0,
      income: 0,
      custoAdministrativo: 0,
      custoOperacional: 0,
      lucroDesejado: 0,
      despesasFiscais: 0,
      comissao: 0,
      custoFinanceiro: 0,
      saldoPeriodo: 0,
      saldoAcumulado: 0,
    });

    return { mensais: resultado, totais };
  };

  const totaisMensaisComCustosELucroBase = useMemo(
    () => calcularFluxoMensal(valorTotalIncome),
    [
      indicesColunas,
      linhasGastos,
      linhasIncome,
      valoresPorLinhaEColuna,
      valorTotalIncome,
      taxaEncargos,
      taxaDespesasFiscais,
      taxaComissao,
      taxaCustoFinanceiro,
      taxaAdministrativa,
      taxaLucro,
    ]
  );

  const totaisMensaisComCustosELucro = totaisMensaisComCustosELucroBase.mensais;
  const totalPeriodoGasto = totaisMensaisComCustosELucroBase.totais.gasto;
  const totalPeriodoSubtotalProjeto = totaisMensaisComCustosELucroBase.totais.subtotalProjeto;
  const totalPeriodoIncome = totaisMensaisComCustosELucroBase.totais.income;
  const totalPeriodoCustoAdministrativo = totaisMensaisComCustosELucroBase.totais.custoAdministrativo;
  const totalPeriodoCustoOperacional = totaisMensaisComCustosELucroBase.totais.custoOperacional;
  const totalPeriodoLucroDesejado = totaisMensaisComCustosELucroBase.totais.lucroDesejado;
  const totalPeriodoDespesasFiscais = totaisMensaisComCustosELucroBase.totais.despesasFiscais;
  const totalPeriodoComissao = totaisMensaisComCustosELucroBase.totais.comissao;
  const totalPeriodoCustoFinanceiro = totaisMensaisComCustosELucroBase.totais.custoFinanceiro;
  const totalPeriodoSubtotalProjetoCorrigido = totalPeriodoCustoOperacional + totalPeriodoLucroDesejado;
  const precoProposta = fatorReceita > 0
    ? (totalPeriodoSubtotalProjeto / fatorReceita)
    : 0;
  const colSpanTotais = Math.max(1, indicesColunas.length);

  ////////////////////////////////////////////CORRIGIDOS//////////////////////////////////////////////////////////////////////////

  const resultadoCorrecaoIterativa = useMemo(() => {
    if (fatorReceita <= 0) {
      return {
        precoFinalPropostaCalculado: 0,
        precoPropostaComCustoFinanceiroCalculado: totalPeriodoSubtotalProjetoCorrigido,
        totaisMensaisComCustosELucroCorrigidoCalculado: calcularFluxoMensal(0),
      };
    }

    const toleranciaSaldoFinal = 0.00001;
    const maxIteracoes = 500;
    let precoAtual = (totalPeriodoSubtotalProjetoCorrigido + totalPeriodoCustoFinanceiro) / fatorReceita;
    let fluxoAtual = calcularFluxoMensal(precoAtual);

    for (let iteracao = 0; iteracao < maxIteracoes; iteracao += 1) {
      console.log(`Iteração ${iteracao + 1}: Preço atual = ${precoAtual.toFixed(2)}, Saldo final = ${fluxoAtual.totais.saldoAcumulado.toFixed(2)}`, fluxoAtual);
      const saldoFinal = fluxoAtual.totais.saldoAcumulado;
      if (Math.abs(saldoFinal) <= toleranciaSaldoFinal) break;

      const proximoPreco = (totalPeriodoSubtotalProjetoCorrigido + fluxoAtual.totais.custoFinanceiro) / fatorReceita;
      if (!Number.isFinite(proximoPreco)) break;
      if (Math.abs(proximoPreco - precoAtual) <= toleranciaSaldoFinal / 10) {
        precoAtual = proximoPreco;
        fluxoAtual = calcularFluxoMensal(precoAtual);
        break;
      }

      precoAtual = proximoPreco;
      fluxoAtual = calcularFluxoMensal(precoAtual);
    }

    return {
      precoFinalPropostaCalculado: precoAtual,
      precoPropostaComCustoFinanceiroCalculado: totalPeriodoSubtotalProjetoCorrigido + fluxoAtual.totais.custoFinanceiro,
      totaisMensaisComCustosELucroCorrigidoCalculado: fluxoAtual,
    };
  }, [
    fatorReceita,
    totalPeriodoSubtotalProjetoCorrigido,
    totalPeriodoCustoFinanceiro,
    indicesColunas,
    linhasGastos,
    linhasIncome,
    valoresPorLinhaEColuna,
    valorTotalIncome,
    taxaEncargos,
    taxaDespesasFiscais,
    taxaComissao,
    taxaCustoFinanceiro,
    taxaAdministrativa,
    taxaLucro,
  ]);

  const precoFinalProposta = resultadoCorrecaoIterativa.precoFinalPropostaCalculado;
  const precoPropostaComCustoFinanceiro = resultadoCorrecaoIterativa.precoPropostaComCustoFinanceiroCalculado;
  const totaisMensaisComCustosELucroCorrigido = resultadoCorrecaoIterativa.totaisMensaisComCustosELucroCorrigidoCalculado.mensais;
  const totalPeriodoDespesasFiscaisCorrigido = resultadoCorrecaoIterativa.totaisMensaisComCustosELucroCorrigidoCalculado.totais.despesasFiscais;
  const totalPeriodoComissaoCorrigido = resultadoCorrecaoIterativa.totaisMensaisComCustosELucroCorrigidoCalculado.totais.comissao;
  const totalPeriodoCustoFinanceiroCorrigido = resultadoCorrecaoIterativa.totaisMensaisComCustosELucroCorrigidoCalculado.totais.custoFinanceiro;

  const stickyPrimeiraColunaStyle = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    backgroundColor: '#fff',
  };

  const stickyPrimeiraColunaRodapeStyle = {
    ...stickyPrimeiraColunaStyle,
    backgroundColor: '#f8f9fa',
    zIndex: 3,
  };

  const margemContribuicao = (totalPeriodoSubtotalProjeto && valorTotalIncome)
    ? `${(((precoFinalProposta - (custosSemEncargos + visaoGeralValorTaxaEncargos + totalPeriodoDespesasFiscaisCorrigido + totalPeriodoCustoFinanceiroCorrigido)) / precoFinalProposta) * 100).toFixed(2)} %`
    : '0 %';

  const itensOrcamentariosTotaisPorGrupo = useMemo(() => {
    const totais = {
      1: 0,
      4: 0,
      2: 0,
      3: 0,
      5: 0,
    };

    (linhasGastos || []).forEach((linha) => {
      const groupId = Number(linha?.propItemGr_id);
      if (!Object.prototype.hasOwnProperty.call(totais, groupId)) return;

      const qtd = toNumber(linha?.propOrcItem_qtd || 0);
      const vlr = toNumber(linha?.[chaveValorGasto] || 0)
      totais[groupId] += (vlr * qtd);
    });

    return totais;
  }, [linhasGastos, chaveValorGasto]);

  const detalhamentoResumoPorGrupo = [
    { label: 'Recursos humanos', value: itensOrcamentariosTotaisPorGrupo[1] || 0 },
    { label: 'Logística', value: itensOrcamentariosTotaisPorGrupo[4] || 0 },
    { label: 'Equipamentos', value: itensOrcamentariosTotaisPorGrupo[2] || 0 },
    { label: 'Despesas gerais', value: itensOrcamentariosTotaisPorGrupo[3] || 0 },
    { label: 'Serviços terceirizados', value: itensOrcamentariosTotaisPorGrupo[5] || 0 },
  ];

  const resumoFinanceiroCards = [
    { label: 'Encargos sociais', value: currencyFormatter.format(visaoGeralValorTaxaEncargos) },
    { label: 'Lucro', value: currencyFormatter.format(totalPeriodoLucroDesejado) },
    { label: 'Administrativo', value: currencyFormatter.format(totalPeriodoCustoAdministrativo) },
    { label: 'Comissao', value: currencyFormatter.format(totalPeriodoComissaoCorrigido) },
    { label: 'Impostos', value: currencyFormatter.format(totalPeriodoDespesasFiscaisCorrigido) },
    { label: 'Margem de contribuicao', value: margemContribuicao },
    { label: 'Custo financeiro', value: currencyFormatter.format(totalPeriodoCustoFinanceiroCorrigido) },
  ];


  const formatarAliquota = (valor) => {
    const numero = Number(valor);
    if (!Number.isFinite(numero)) return '0';
    return numero.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  };

  const formatarPercentual = (valor) => {
    const numero = Number(valor);
    if (!Number.isFinite(numero)) return '0,00';
    return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const aliquotaEncargos = formatarAliquota(taxaEncargos);
  const aliquotaAdministrativa = formatarAliquota(taxaAdministrativa);
  const aliquotaCustoFinanceiro = formatarAliquota(taxaCustoFinanceiro);
  const aliquotaImpostos = formatarAliquota(taxaDespesasFiscais);
  const aliquotaComissao = formatarAliquota(taxaComissao);

  const formatarParticipacao = (valor, base) => {
    const numerador = Number(valor) || 0;
    const denominador = Number(base) || 0;
    if (!denominador) return '';
    return `${formatarPercentual((numerador / denominador) * 100)}%`;
  };

  const dreReceita = Number(precoFinalProposta) || 0;
  const dreBaseCustos = Number(custosSemEncargos) || 0;
  const dreEncargos = Number(visaoGeralValorTaxaEncargos) || 0;
  const dreAdministrativo = Number(totalPeriodoCustoAdministrativo) || 0;
  const dreCustoFinanceiro = Number(totalPeriodoCustoFinanceiroCorrigido) || 0;
  const dreImpostos = Number(totalPeriodoDespesasFiscaisCorrigido) || 0;
  const dreComissao = Number(totalPeriodoComissaoCorrigido) || 0;
  const dreMargemValor = dreReceita - dreBaseCustos - dreImpostos - dreEncargos - dreCustoFinanceiro;
  const dreLucro = dreMargemValor - dreAdministrativo - dreComissao;

  const linhasDre = [
    {
      sinal: '',
      rotulo: 'Receita',
      valor: currencyFormatter.format(dreReceita),
      percentual: '',
      formula: 'Orçamento final',
      tipo: 'total',
    },
    {
      sinal: '(-)',
      rotulo: 'Base de custos',
      valor: currencyFormatter.format(dreBaseCustos),
      percentual: formatarParticipacao(dreBaseCustos, dreReceita),
      formula: 'Recursos humanos + Logística + Equipamentos + Despesas gerais + Serviços terceirizados',
      tipo: 'linha',
    },
    ...detalhamentoResumoPorGrupo.map((grupo) => ({
      sinal: '',
      rotulo: grupo.label,
      valor: currencyFormatter.format(grupo.value || 0),
      percentual: formatarParticipacao(grupo.value || 0, dreBaseCustos),
      formula: 'componente da Base de custos',
      tipo: 'detalhe',
    })),
    {
      sinal: '(-)',
      rotulo: 'Impostos',
      valor: currencyFormatter.format(dreImpostos),
      percentual: formatarParticipacao(dreImpostos, dreReceita),
      formula: `${aliquotaImpostos}% da Receita`,
      tipo: 'linha',
    },
    {
      sinal: '(-)',
      rotulo: 'Encargos sociais',
      valor: currencyFormatter.format(dreEncargos),
      percentual: formatarParticipacao(dreEncargos, dreReceita),
      formula: `${aliquotaEncargos}% dos recursos humanos CLT da Base de custos`,
      tipo: 'linha',
    },
    {
      sinal: '(-)',
      rotulo: 'Custo financeiro',
      valor: currencyFormatter.format(dreCustoFinanceiro),
      percentual: formatarParticipacao(dreCustoFinanceiro, dreReceita),
      formula: `${aliquotaCustoFinanceiro}% ao mês sobre o saldo do período anterior, quando negativo`,
      tipo: 'linha',
    },
    {
      sinal: '(=)',
      rotulo: 'Margem de contribuição',
      valor: currencyFormatter.format(dreMargemValor),
      percentual: formatarParticipacao(dreMargemValor, dreReceita),
      formula: 'Receita - Base de custos - Impostos - Encargos sociais - Custo financeiro',
      tipo: 'total',
    },
    {
      sinal: '(-)',
      rotulo: 'Administrativo',
      valor: currencyFormatter.format(dreAdministrativo),
      percentual: formatarParticipacao(dreAdministrativo, dreReceita),
      formula: `${aliquotaAdministrativa}% de (Base de custos + Encargos sociais)`,
      tipo: 'linha',
    },
    {
      sinal: '(-)',
      rotulo: 'Comissão',
      valor: currencyFormatter.format(dreComissao),
      percentual: formatarParticipacao(dreComissao, dreReceita),
      formula: `${aliquotaComissao}% da Receita`,
      tipo: 'linha',
    },
    {
      sinal: '(=)',
      rotulo: 'Lucro',
      valor: currencyFormatter.format(dreLucro),
      percentual: formatarParticipacao(dreLucro, dreReceita),
      formula: 'Margem de contribuição - Administrativo - Comissão',
      tipo: 'total',
    },
  ];

  const fluxoMensalGraficoSeries = useMemo(() => ([
    {
      name: 'Receita',
      type: 'column',
      data: totaisMensaisComCustosELucroCorrigido.map((mes) => Number(mes.income.toFixed(2))),
    },
    {
      name: 'Despesa',
      type: 'column',
      data: totaisMensaisComCustosELucroCorrigido.map((mes) => Number((mes.gasto + mes.gastoEquipeCltEncargos).toFixed(2))),
    },
    {
      name: 'Resultado',
      type: 'column',
      data: totaisMensaisComCustosELucroCorrigido.map((mes) => Number(mes.lucroDesejado.toFixed(2))),
    },
    {
      name: 'Acumulado',
      type: 'line',
      data: totaisMensaisComCustosELucroCorrigido.map((mes) => Number(mes.saldoAcumulado.toFixed(2))),
    },
  ]), [totaisMensaisComCustosELucroCorrigido]);

  const fluxoMensalGraficoOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
    },
    colors: ['#1cbb8c', '#f46a6a', '#5664d2', '#3fa3ff'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
    },
    stroke: {
      show: true,
      width: [0, 0, 0, 4],
      curve: 'smooth',
    },
    markers: {
      size: [0, 0, 0, 4],
      hover: {
        size: 6,
      },
    },
    xaxis: {
      categories: totaisMensaisComCustosELucroCorrigido.map((mes) => `Mês ${mes.indiceColuna}`),
    },
    yaxis: {
      labels: {
        formatter: (value) => currencyFormatter.format(value),
      },
      title: {
        text: 'Valor',
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => currencyFormatter.format(value),
      },
    },
  }), [totaisMensaisComCustosELucroCorrigido]);

  useEffect(() => {
    if (typeof setPrecoFinalProposta === 'function') {
      setPrecoFinalProposta(Number(precoFinalProposta.toFixed(2)));
    }
    if (typeof setResumoValoresProposta === 'function') {
      setResumoValoresProposta({
        precoProposta: Number(precoProposta.toFixed(2)),
        precoPropostaComCustoFinanceiro: Number(precoPropostaComCustoFinanceiro.toFixed(2)),
      });
    }
  }, [precoFinalProposta, precoProposta, precoPropostaComCustoFinanceiro, setPrecoFinalProposta, setResumoValoresProposta]);

  return (
    <div>
      <Card style={{ marginBottom: '1rem' }}>
        <CardBody>
          <h4 className="card-title mb-4">Fluxo mensal da proposta</h4>
          <div id="fluxo-mensal-proposta-chart" className="apex-charts" dir="ltr">
            <ReactApexChart
              options={fluxoMensalGraficoOptions}
              series={fluxoMensalGraficoSeries}
              type="bar"
              height="280"
            />
          </div>
        </CardBody>
      </Card>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={mostrarDetalhamentoTotalPeriodo}
            onChange={(event) => setMostrarDetalhamentoTotalPeriodo(event.target.checked)}
          />
          Mostrar informações da base de cálculo
        </label>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Fluxo mensal
            </th>
            {indicesColunas.map((indiceColuna) => (
              <th
                key={indiceColuna}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {indiceColuna}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Receita
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`income-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.income)}
              </td>
            ))}
          </tr>

          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Gasto do mês
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`gasto-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.gasto + mes.gastoEquipeCltEncargos)}
              </td>
            ))}
          </tr>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Gasto equipe CLT (encargos)
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`gasto-equipe-clt-encargos-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.gastoEquipeCltEncargos)}
              </td>
            ))}
          </tr>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Custos diretos
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`custos-diretos-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.custosDiretos)}
              </td>
            ))}
          </tr>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Custo administrativo
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`custo-administrativo-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.custoAdministrativo)}
              </td>
            ))}
          </tr>

          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Custo operacional
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`custo-operacional-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.custoOperacional)}
              </td>
            ))}
          </tr>

          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Lucro desejado
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`lucro-desejado-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.lucroDesejado)}
              </td>
            ))}
          </tr>

          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Despesas fiscais
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`despesas-fiscais-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.despesasFiscais)}
              </td>
            ))}
          </tr>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Comissao
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`comissao-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.comissao)}
              </td>
            ))}
          </tr>
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Custo financeiro
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`custo-financeiro-${mes.indiceColuna}`}
                style={{ border: '1px solid #ced4da', padding: '6px 8px', textAlign: 'center' }}
              >
                {currencyFormatter.format(mes.custoFinanceiro)}
              </td>
            ))}
          </tr>

          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 600,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Saldo do periodo
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`saldo-periodo-${mes.indiceColuna}`}
                style={{
                  border: '1px solid #ced4da',
                  padding: '6px 8px',
                  textAlign: 'center',
                  fontWeight: 700,
                  color: mes.saldoPeriodo < 0 ? '#dc3545' : '#198754',
                }}
              >
                {currencyFormatter.format(mes.saldoPeriodo)}
              </td>
            ))}
          </tr>

          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 600,
                minWidth: '260px',
                ...stickyPrimeiraColunaStyle,
              }}
            >
              Saldo acumulado
            </th>
            {totaisMensaisComCustosELucroCorrigido.map((mes) => (
              <td
                key={`saldo-acumulado-${mes.indiceColuna}`}
                style={{
                  border: '1px solid #ced4da',
                  padding: '6px 8px',
                  textAlign: 'center',
                  fontWeight: 700,
                  color: mes.saldoAcumulado < 0 ? '#dc3545' : '#198754',
                }}
              >
                {currencyFormatter.format(mes.saldoAcumulado)}
              </td>
            ))}
          </tr>

          {mostrarDetalhamentoTotalPeriodo && (
          <tr>
            <th
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                textAlign: 'left',
                backgroundColor: '#f8f9fa',
                minWidth: '260px',
                ...stickyPrimeiraColunaRodapeStyle,
              }}
            >
              Total do periodo
            </th>
            <td
              colSpan={colSpanTotais}
              style={{
                border: '1px solid #ced4da',
                padding: '6px 8px',
                backgroundColor: '#f8f9fa',
              }}
            >
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Base de custos: </strong>
                <span>{currencyFormatter.format(totalPeriodoGasto)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Gasto equipe CLT (encargos): </strong>
                <span>
                  {
                    currencyFormatter
                    .format(
                      //totalPeriodoGastoEquipeCltEncargos
                      visaoGeralValorTaxaEncargos
                    )
                  }
                </span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Custos diretos: </strong>
                <span>{currencyFormatter.format(custosSemEncargos)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Custo administrativo: </strong>
                <span>{currencyFormatter.format(totalPeriodoCustoAdministrativo)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Custo operacional: </strong>
                <span>{currencyFormatter.format(totalPeriodoCustoOperacional)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Lucro desejado: </strong>
                <span>{currencyFormatter.format(totalPeriodoLucroDesejado)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Subtotal da proposta (subtotal 1): </strong>
                <span>{currencyFormatter.format(totalPeriodoSubtotalProjeto)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Preco da proposta (total 1): </strong>
                <span>{currencyFormatter.format(precoProposta)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Ganhos nao corrigidos: </strong>
                <span>{currencyFormatter.format(totalPeriodoIncome)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Despesas fiscais nao corrigidas: </strong>
                <span>{currencyFormatter.format(totalPeriodoDespesasFiscais)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Comissao nao corrigida: </strong>
                <span>{currencyFormatter.format(totalPeriodoComissao)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Custo financeiro: </strong>
                <span>{currencyFormatter.format(totalPeriodoCustoFinanceiroCorrigido)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Preco da proposta + custo financeiro (subtotal 2): </strong>
                <span>{currencyFormatter.format(precoPropostaComCustoFinanceiro)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Preco final da proposta (total 2): </strong>
                <span>{currencyFormatter.format(precoFinalProposta)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Despesas fiscais corrigidas: </strong>
                <span>{currencyFormatter.format(totalPeriodoDespesasFiscaisCorrigido)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Comissao corrigida: </strong>
                <span>{currencyFormatter.format(totalPeriodoComissaoCorrigido)}</span>
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', width: '100%' }}>
                <strong>Margem de contribuicao: </strong>
                <span>
                  {margemContribuicao}
                </span>
              </div>

            </td>
          </tr>
          )}
        </tbody>
        </table>
      </div>
      <div
        style={{
          marginTop: '1rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '8px',
        }}
      >
        <Card style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa', marginBottom: 0, height: '100%' }}>
          <CardBody style={{ padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '10px' }}>
              <strong>Base de custos:</strong>
              <span>{currencyFormatter.format(custosSemEncargos)}</span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '8px 12px',
                fontSize: '12px',
              }}
            >
              {detalhamentoResumoPorGrupo.map((item) => (
                <div key={item.label} style={{ minWidth: 0 }}>
                  <small style={{ display: 'block', color: '#6c757d' }}>{item.label}</small>
                  <strong>{currencyFormatter.format(item.value)}</strong>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
        {resumoFinanceiroCards.map((item) => (
          <Card key={item.label} style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa', marginBottom: 0, height: '100%' }}>
            <CardBody
              style={{
                padding: '10px 12px',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }}
            >
              <strong>{item.label}:</strong>
              <span>{item.value}</span>
            </CardBody>
          </Card>
        ))}
        <Card
          style={{
            border: '1px solid #ced4da',
            backgroundColor: '#e9ecef',
            gridColumn: '1 / -1',
            marginBottom: 0,
          }}
        >
          <CardBody
            style={{
              padding: '10px 12px',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <strong>Orçamento final:</strong>
            <span>{currencyFormatter.format(precoFinalProposta)}</span>
          </CardBody>
        </Card>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>DRE</div>
        <table style={{ borderCollapse: 'collapse', width: '100%', backgroundColor: '#fff' }}>
                    <tbody>
            {linhasDre.map((linha) => {
              const fundo = linha.tipo === 'total'
                ? '#e9ecef'
                : (linha.tipo === 'subtotal' ? '#f8f9fa' : '#fff');
              const peso = linha.tipo === 'linha' || linha.tipo === 'info' || linha.tipo === 'detalhe' ? 400 : 700;
              return (
                <tr key={linha.rotulo} style={linha.tipo === 'detalhe' ? { fontSize: '12px' } : undefined}>
                  <td style={{ border: '1px solid #ced4da', padding: '8px 10px', textAlign: 'center', color: '#6c757d', backgroundColor: fundo, fontWeight: peso }}>
                    {linha.sinal}
                  </td>
                  <td style={{ border: '1px solid #ced4da', padding: '8px 10px', paddingLeft: linha.tipo === 'detalhe' ? '28px' : '10px', backgroundColor: fundo, color: linha.tipo === 'detalhe' ? '#495057' : 'inherit', fontWeight: linha.tipo === 'detalhe' ? 400 : (linha.tipo === 'linha' ? 600 : peso) }}>
                    {linha.rotulo}
                  </td>
                  <td style={{ border: '1px solid #ced4da', padding: '8px 10px', textAlign: 'right', backgroundColor: fundo, fontWeight: peso, whiteSpace: 'nowrap' }}>
                    {linha.valor}
                  </td>
                  <td style={{ border: '1px solid #ced4da', padding: '8px 10px', textAlign: 'right', backgroundColor: fundo, fontWeight: peso, whiteSpace: 'nowrap', color: '#495057', width: '84px' }}>
                    {linha.percentual}
                  </td>
                  <td style={{ border: '1px solid #ced4da', padding: '8px 10px', backgroundColor: fundo, color: '#495057', fontSize: linha.tipo === 'detalhe' ? '12px' : '13px' }}>
                    {linha.formula}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default TabelaCruzamentoFluxoMensalPropTemporalEmMeses;
